import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { fetchFrontDeskBoard } from "../services/api";
import { useT } from "../i18n/useT";

// Front Desk — Phase A2: the live board that opens the department. Today's
// arrivals, rooms still to assign, in-house, departures and the waiting list,
// with counts. Actions (check-in, assign room, reinstate) live on Reservations.
const S = {
  en: {
    title: "Front desk", today: "Today", open: "Open",
    arrivals: "Arrivals", to_assign: "To assign", inhouse: "In-house",
    departures: "Departures", waitlist: "Waiting list", checked_out: "Checked out",
    none: "Nothing here right now.", hold6: "6PM hold", needs_room: "No room",
    nights: "nights", go_res: "Go to reservations",
  },
  am: {
    title: "የፊት ዴስክ", today: "ዛሬ", open: "ክፈት",
    arrivals: "መግቢያዎች", to_assign: "ክፍል የሚመደብ", inhouse: "በውስጥ ያሉ",
    departures: "መውጫዎች", waitlist: "የመጠባበቂያ ዝርዝር", checked_out: "የወጡ",
    none: "አሁን ምንም የለም።", hold6: "6PM ማቆያ", needs_room: "ክፍል የለም",
    nights: "ሌሊቶች", go_res: "ወደ ቦታ ማስያዣ ሂድ",
  },
  om: {
    title: "Fuula duraa", today: "Har'a", open: "Bani",
    arrivals: "Ga'umsa", to_assign: "Kutaa ramaduu", inhouse: "Keessa jiran",
    departures: "Ba'umsa", waitlist: "Tarree eeggannoo", checked_out: "Kan ba'an",
    none: "Amma homaa hin jiru.", hold6: "Eeggannoo 6PM", needs_room: "Kutaa hin jiru",
    nights: "halkanoota", go_res: "Gara booking deemi",
  },
};

const CARD = { background: "#fff", border: "1px solid #eef2f2", borderRadius: 14, boxShadow: "0 1px 3px rgba(6,59,72,0.06)" };
const TILE = (accent) => ({ ...CARD, padding: "14px 16px", flex: "1 1 140px", minWidth: 130, borderTop: `3px solid ${accent}` });
const badge = (bg, fg) => ({ fontSize: 11, fontWeight: 700, background: bg, color: fg, borderRadius: 20, padding: "2px 8px" });

export default function FrontDeskBoardPage() {
  const { lang } = useT();
  const t = (k) => (S[lang] && S[lang][k]) || S.en[k] || k;
  const [data, setData] = useState(null);

  useEffect(() => { fetchFrontDeskBoard().then(setData).catch(() => setData({ counts: {}, arrivals: [], to_assign: [], inhouse: [], departures: [], waitlist: [] })); }, []);

  const c = (data && data.counts) || {};
  const tiles = [
    { k: "arrivals", n: c.arrivals, accent: "#0F6E56" },
    { k: "to_assign", n: c.to_assign, accent: "#B8860B" },
    { k: "inhouse", n: c.inhouse, accent: "#063B48" },
    { k: "departures", n: c.departures, accent: "#2563A8" },
    { k: "waitlist", n: c.waitlist, accent: "#8A6D3B" },
    { k: "checked_out", n: c.checked_out_today, accent: "#8A949B" },
  ];

  const Row = ({ r }) => (
    <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 4px", borderBottom: "1px solid #f2f5f5" }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{r.guest_name}</div>
        <div className="muted" style={{ fontSize: 12.5 }}>
          {r.room_name ? `#${r.room_name}` : (r.room_type_name || "—")}
          {" · "}{r.check_in_date} → {r.check_out_date}
          {r.meal_plan ? ` · ${r.meal_plan.toUpperCase()}` : ""}
        </div>
      </div>
      {r.is_6pm_hold && <span style={badge("#FBF2DC", "#8A6D3B")}>{t("hold6")}</span>}
      {r.needs_room && <span style={badge("#FDECEC", "#A32D2D")}>{t("needs_room")}</span>}
      <Link className="btn secondary small" to={`/front-desk/reservations?focus=${r.id}`} style={{ textDecoration: "none" }}>{t("open")}</Link>
    </div>
  );

  const Section = ({ k, rows }) => (
    <div style={{ ...CARD, padding: 14 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 6 }}>
        <h3 style={{ margin: 0 }}>{t(k)}</h3>
        <span className="muted" style={{ fontSize: 13 }}>{(rows || []).length}</span>
      </div>
      {rows && rows.length ? rows.map((r) => <Row key={r.id} r={r} />)
        : <div className="muted" style={{ fontSize: 13, padding: "8px 4px" }}>{t("none")}</div>}
    </div>
  );

  return (
    <div style={{ maxWidth: 980, margin: "0 auto" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", flexWrap: "wrap", gap: 8 }}>
        <h1 style={{ margin: "0 0 2px" }}>{t("title")}</h1>
        <span className="muted" style={{ fontSize: 14 }}>{t("today")} · {data ? data.date : "…"}</span>
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginTop: 12 }}>
        {tiles.map((x) => (
          <div key={x.k} style={TILE(x.accent)}>
            <div style={{ fontSize: 26, fontWeight: 800, color: x.accent }}>{x.n != null ? x.n : "…"}</div>
            <div className="muted" style={{ fontSize: 13 }}>{t(x.k)}</div>
          </div>
        ))}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: 12, marginTop: 14 }}>
        <Section k="arrivals" rows={data && data.arrivals} />
        <Section k="to_assign" rows={data && data.to_assign} />
        <Section k="departures" rows={data && data.departures} />
        <Section k="waitlist" rows={data && data.waitlist} />
      </div>

      <div style={{ marginTop: 14 }}>
        <Link className="btn primary" to="/front-desk/reservations" style={{ textDecoration: "none" }}>{t("go_res")} →</Link>
      </div>
    </div>
  );
}
